import { FlowDefinitionTemplateVisibility, FlowTagCategory, type FlowTagRecord } from 'flow-schema';
import { groupBy, mapValues } from 'lodash';

import { type api, isDefined, isTruthy } from 'common';

import { OTHER_TAG_NAME } from './constants';

type FlowDefinitionTemplate = api.FlowDefinitionTemplate.FlowDefinitionTemplate;
type TemplateTag = FlowTagRecord;
type TemplateTagCategory = FlowTagCategory;
export type TemplateTagByCategory = Partial<Record<TemplateTagCategory, TemplateTag[]>>;
export type TemplateTagNameByCategory = Partial<Record<TemplateTagCategory, string[]>>;
export type UniqueTemplateTagNameByCategory = Partial<Record<TemplateTagCategory, Set<string>>>;
export const FLOW_TEMPLATE_VISIBILITY_LABELS: Record<FlowDefinitionTemplateVisibility, string> = {
  [FlowDefinitionTemplateVisibility.Global]: 'Global',
  [FlowDefinitionTemplateVisibility.Organization]: 'Organization',
  [FlowDefinitionTemplateVisibility.Private]: 'Draft (Private)',
};
const TemplateTagCategory = FlowTagCategory;
const TEMPLATE_TAG_CATEGORIES = [
  TemplateTagCategory.SolutionArea,
  TemplateTagCategory.UseCase,
  TemplateTagCategory.FlowType,
  TemplateTagCategory.SupportedEhr,
];

function groupTagsByCategory(tags?: TemplateTag[] | null): TemplateTagByCategory {
  return groupBy(tags, (tag) => tag.category);
}

export function parseTemplateTags(
  template: Pick<FlowDefinitionTemplate, 'tags'>
): TemplateTagNameByCategory {
  return mapValues(
    groupTagsByCategory(template.tags),
    (tags) => tags?.map((tag) => tag.name) ?? []
  );
}

export function getUniqueTagsByCategoryFromTemplates(
  templates: Pick<FlowDefinitionTemplate, 'tags'>[]
): UniqueTemplateTagNameByCategory {
  const tags = templates.flatMap((template) => template.tags ?? []);
  return mapValues(
    groupTagsByCategory(tags),
    (tags) => new Set(tags?.map((tag) => tag.name) ?? [])
  );
}

export function doesTemplateMatchSelectedTags(args: {
  template: Pick<FlowDefinitionTemplate, 'tags'>;
  selectedTagsByCategory: UniqueTemplateTagNameByCategory;
}): boolean {
  const { template, selectedTagsByCategory } = args;
  const templateTags = template.tags ?? [];
  for (const [category, selectedTags] of Object.entries(selectedTagsByCategory)) {
    if (!isTruthy(selectedTags.size)) {
      continue;
    }
    const matches = templateTags.some(
      (tag) => tag.category === category && selectedTags.has(tag.name)
    );
    if (!matches) {
      return false;
    }
  }
  return true;
}

export function getFacetedTagsByCategoryFromTemplates(args: {
  templates: Pick<FlowDefinitionTemplate, 'tags'>[];
  selectedTagsByCategory: UniqueTemplateTagNameByCategory;
}): UniqueTemplateTagNameByCategory {
  const { templates, selectedTagsByCategory } = args;
  const facetedTagsByCategory: UniqueTemplateTagNameByCategory = {};

  for (const mainCategory of TEMPLATE_TAG_CATEGORIES) {
    const selectedTagsInOtherCategories = Object.fromEntries(
      Object.entries(selectedTagsByCategory).filter(
        ([category]) => (category as TemplateTagCategory) !== mainCategory
      )
    );

    const matchingTemplatesInOtherCategories = templates.filter((template) =>
      doesTemplateMatchSelectedTags({
        template,
        selectedTagsByCategory: selectedTagsInOtherCategories,
      })
    );

    const uniqueTagsByCategory = getUniqueTagsByCategoryFromTemplates(
      matchingTemplatesInOtherCategories
    );
    const uniqueTagsInMainCategory = uniqueTagsByCategory[mainCategory] ?? new Set<string>();

    const selectedTagsInMainCategory = selectedTagsByCategory[mainCategory] ?? new Set<string>();

    facetedTagsByCategory[mainCategory] = new Set([
      ...uniqueTagsInMainCategory,
      ...selectedTagsInMainCategory,
    ]);
  }

  return facetedTagsByCategory;
}

export function compareTemplatesByFeaturedTimestamp(
  templateA: FlowDefinitionTemplate,
  templateB: FlowDefinitionTemplate
): number {
  const timestampA = templateA.metadata?.featuredTimestamp;
  const timestampB = templateB.metadata?.featuredTimestamp;

  // If both have timestamps, sort by timestamp (newest first)
  if (isDefined(timestampA) && isDefined(timestampB)) {
    return new Date(timestampB).getTime() - new Date(timestampA).getTime();
  }

  // Templates with timestamps come first, then sort by name
  if (isDefined(timestampA) && !isDefined(timestampB)) {
    return -1;
  }
  if (!isDefined(timestampA) && isDefined(timestampB)) {
    return 1;
  }
  return templateA.name.localeCompare(templateB.name);
}

/**
 * Decodes URL-encoded characters in the pathname of a URL
 */
export function decodeUrlPath(url: string): string {
  try {
    const urlObj = new URL(url);
    urlObj.pathname = decodeURIComponent(urlObj.pathname);
    return urlObj.toString();
  } catch {
    return url;
  }
}

export function sortFlowTemplateTagNames(tagA: string, tagB: string): number {
  if (tagA === tagB) {
    return 0;
  }
  if (tagA === OTHER_TAG_NAME) {
    return 1;
  }
  if (tagB === OTHER_TAG_NAME) {
    return -1;
  }
  return tagA.localeCompare(tagB);
}
